/**
 * Copy button with visual feedback showing "Copied!" state.
 */

"use client";

import { useState, useCallback } from "react";
import { Button, type ButtonProps } from "./button";
import { Copy, Check } from "lucide-react";
import { cn } from "../lib/utils";

interface CopyButtonProps extends Omit<ButtonProps, "onClick"> {
  /** Text to copy to clipboard */
  text: string;
  /** Label to show (defaults to showing icon only) */
  label?: string;
  /** Duration in ms to show "Copied!" state (default: 2000) */
  feedbackDuration?: number;
  /** Callback after successful copy */
  onCopy?: () => void;
}

/**
 * Button that copies text to clipboard with visual feedback
 *
 * @example
 * <CopyButton text="hello world" />
 * <CopyButton text={email} label="Copy Email" variant="outline" />
 */
export function CopyButton({
  text,
  label,
  feedbackDuration = 2000,
  onCopy,
  className,
  variant = "ghost",
  size = "sm",
  ...props
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      onCopy?.();

      setTimeout(() => {
        setCopied(false);
      }, feedbackDuration);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  }, [text, feedbackDuration, onCopy]);

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleCopy}
      className={cn(
        "transition-all",
        copied && "text-chart-4 hover:text-chart-4",
        className
      )}
      {...props}
    >
      {copied ? (
        <>
          <Check className="h-4 w-4" />
          {label && <span className="ml-1">Copied!</span>}
        </>
      ) : (
        <>
          <Copy className="h-4 w-4" />
          {label && <span className="ml-1">{label}</span>}
        </>
      )}
    </Button>
  );
}

/**
 * Inline copy button for use next to text (smaller, more subtle)
 */
export function InlineCopyButton({
  text,
  className,
  onCopy,
}: {
  text: string;
  className?: string;
  onCopy?: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      onCopy?.();

      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  }, [text, onCopy]);

  return (
    <button
      onClick={handleCopy}
      className={cn(
        "inline-flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors",
        copied && "text-chart-4 hover:text-chart-4",
        className
      )}
      title={copied ? "Copied!" : "Copy to clipboard"}
    >
      {copied ? (
        <Check className="h-3 w-3" />
      ) : (
        <Copy className="h-3 w-3" />
      )}
    </button>
  );
}

/**
 * Text with copy button - displays text with a copy icon
 */
export function CopyableText({
  text,
  truncate,
  className,
  mono = false,
}: {
  text: string;
  truncate?: number;
  className?: string;
  mono?: boolean;
}) {
  const displayText = truncate && text.length > truncate
    ? `${text.slice(0, truncate)}...`
    : text;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1",
        mono && "font-mono text-sm",
        className
      )}
    >
      <span title={text}>{displayText}</span>
      <InlineCopyButton text={text} />
    </span>
  );
}
